"use client";

import { CLink } from "@/components/common/customLink/CustomLink";
import Button from "@/components/ui/button/Button";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { BrandLogoSkeleton } from "./BrandLogoSkeleton";

interface BrandLogo {
  url: string;
  altText?: string;
}

interface CarouselBrand {
  _key: string;
  logo?: BrandLogo;
  link?: { url?: string };
}

interface LogoCarouselProps {
  brands: CarouselBrand[];
  title?: string;
  viewAllLabel?: string;
  viewAllHref?: string;
  locale: string;
}

/**
 * Responsive layout config — 2 rows always, columns adapt to breakpoint:
 * - Mobile  (< 768px):  2 cols × 2 rows = 4 per slide
 * - Tablet  (≥ 768px):  4 cols × 2 rows = 8 per slide
 * - Desktop (≥ 1024px): 6 cols × 2 rows = 12 per slide
 */
function getColsAndPageSize(width: number): { cols: number; pageSize: number } {
  if (width >= 1024) return { cols: 6, pageSize: 12 };
  if (width >= 768) return { cols: 4, pageSize: 8 };
  return { cols: 2, pageSize: 4 };
}

interface Page {
  key: string;
  columns: { top: CarouselBrand; bottom?: CarouselBrand }[];
}

function buildPages(brands: CarouselBrand[], cols: number, pageSize: number): Page[] {
  const pages: Page[] = [];
  for (let i = 0; i < brands.length; i += pageSize) {
    const chunk = brands.slice(i, i + pageSize);
    const topRow = chunk.slice(0, cols);
    const bottomRow = chunk.slice(cols);

    const columns: { top: CarouselBrand; bottom?: CarouselBrand }[] = topRow.map(
      (brand, j) => ({
        top: brand,
        bottom: bottomRow[j] as CarouselBrand | undefined,
      }),
    );

    // Pad to exactly `cols` columns so every page is the same width
    while (columns.length < cols) {
      columns.push({
        top: { _key: `__pad-${i}-${columns.length}` },
        bottom: undefined,
      });
    }

    pages.push({ key: `page-${i}`, columns });
  }
  return pages;
}

export default function LogoCarousel({
  brands,
  title,
  viewAllLabel,
  viewAllHref = "",
  locale,
}: LogoCarouselProps) {
  const isRTL = locale === "ar";

  const [cols, setCols] = useState(6);
  const [pageSize, setPageSize] = useState(12);

  useEffect(() => {
    const update = () => {
      const { cols: c, pageSize: p } = getColsAndPageSize(window.innerWidth);
      setCols(c);
      setPageSize(p);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    containScroll: false,
    dragFree: false,
    direction: "ltr",
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setTimeout(() => onSelect(), 0);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, cols, pageSize]);

  const pages = buildPages(brands, cols, pageSize);

  const gridColsClass =
    cols === 6 ? "grid-cols-6" : cols === 4 ? "grid-cols-4" : "grid-cols-2";

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Carousel area with arrows */}
      <div className="relative px-0 md:px-12">
        <div ref={emblaRef} className="overflow-hidden" dir="ltr">
          <div className="flex">
            {pages.map((page) => (
              <div key={page.key} className="flex-shrink-0 w-full min-w-0">
                <div className={`grid ${gridColsClass}`}>
                  {page.columns.map((col, colIndex) => (
                    <div
                      key={col.top._key}
                      className="flex flex-col gap-y-4 md:gap-y-8"
                    >
                      <BrandLogoCell brand={col.top} priority={colIndex < cols} />
                      {col.bottom ? (
                        <BrandLogoCell brand={col.bottom} priority={false} />
                      ) : (
                        <div className="h-20 md:h-24" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Left Arrow */}
        <button
          onClick={() => isRTL ? emblaApi?.scrollNext() : emblaApi?.scrollPrev()}
          disabled={isRTL ? !canScrollNext : !canScrollPrev}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 disabled:opacity-30 hover:opacity-70 transition-opacity hidden md:block"
          aria-label="Previous"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => isRTL ? emblaApi?.scrollPrev() : emblaApi?.scrollNext()}
          disabled={isRTL ? !canScrollPrev : !canScrollNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 disabled:opacity-30 hover:opacity-70 transition-opacity hidden md:block"
          aria-label="Next"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* View All Button — only rendered when a CMS link is configured */}
      {viewAllHref && (
        <CLink
          href={viewAllHref}
          className="mx-auto flex w-full max-w-[10.625rem] items-center justify-center pt-7 md:pt-9"
        >
          <Button
            variant="contained"
            className="flex h-[2.5rem] w-full items-center justify-center text-sm font-semibold md:h-[3.125rem]"
          >
            {viewAllLabel || (locale === "ar" ? "عرض الكل" : "View All")}
          </Button>
        </CLink>
      )}
    </div>
  );
}

/* Individual brand logo cell */
function BrandLogoCell({
  brand,
  priority = false,
}: {
  brand: CarouselBrand;
  priority?: boolean;
}) {
  const logoSrc = brand.logo?.url ?? null;
  const [loaded, setLoaded] = useState(false);

  if (brand._key.startsWith("__pad")) {
    return <div className="h-20 md:h-24" />;
  }

  const content = (
    <div className="flex items-center justify-center p-4 h-20 md:h-24 select-none touch-pan-y">
      {logoSrc && (
        <div className="relative w-full h-full max-w-[120px]">
          {!loaded && (
            <BrandLogoSkeleton className="absolute inset-0 w-full h-full rounded-md" />
          )}
          <Image
            src={logoSrc}
            alt="Brand logo"
            fill
            sizes="(max-width: 767px) 45vw, (max-width: 1023px) 25vw, 120px"
            className={`object-contain transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
            draggable={false}
            loading={priority ? undefined : "lazy"}
            priority={priority}
            onLoad={() => setLoaded(true)}
          />
        </div>
      )}
    </div>
  );

  if (brand.link?.url) {
    return (
      <CLink
        href={brand.link.url}
        className="block grayscale hover:grayscale-0 transition-all duration-300"
      >
        {content}
      </CLink>
    );
  }

  return (
    <div className="grayscale hover:grayscale-0 transition-all duration-300">
      {content}
    </div>
  );
}
